import { Select as AntDSelect } from 'antd'
import { useField, useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import { useCallback, useMemo } from 'react'
import { createUseStyles } from 'react-jss'
import { isArray } from 'lodash'
import cn from 'classnames'
import Item from './Item'

const useStyles = createUseStyles({
  fullWidth: {
    '& > div.ant-form-item-control-wrapper:first-child': {
      display: 'block',
    },
  },
  label: {
    display: 'flex',
    alignContent: 'center',
    marginRight: '10px',
  },
  wrapper: {
    display: 'flex',
    alignItems: 'center',
  },
})

const { Option, OptGroup } = AntDSelect

export default function Select({
  containerClassName = undefined,
  id = undefined,
  label = undefined,
  labelPropName = 'name',
  options = undefined,
  helperText = undefined,
  valuePropName = 'id',
  fullWidth = false,
  labelCol = undefined,
  wrapperCol = undefined,
  wrapperItem = true,
  wrapperClassName = undefined,
  layout = 'horizontal',
  dropdownStyle = undefined,
  dropdownWidth = undefined,
  ...rest
}) {
  const [field, { error }, { setValue, setTouched }] = useField(rest)
  const { submitCount } = useFormikContext()
  const hasError = Boolean(submitCount && error)
  const handleBlur = useCallback(() => setTouched(true), [setTouched])

  const classes = useStyles()

  const entryToOption = useCallback(
    entry => {
      if (typeof entry === 'string') {
        return { label: entry, value: entry }
      }
      if (isArray(entry)) {
        return { label: entry[0], value: entry[1] }
      }
      return {
        value: entry[valuePropName],
        label: entry[labelPropName],
      }
    },
    [valuePropName, labelPropName]
  )

  const mappedOptions = useMemo(() => {
    if (!options) {
      return []
    }
    if (isArray(options)) {
      return options.map(entryToOption)
    }
    return Object.entries(options).map(([key, value]) => ({
      label: key,
      options: value.map(entryToOption),
    }))
  }, [entryToOption, options])

  const styles = dropdownStyle ?
    {
      popup: {
        root: { ...dropdownStyle}
      }
    } : undefined

  const sel = () => (
    <div className={cn(classes.wrapper, { [classes.fullWidth]: fullWidth }, wrapperClassName)}>
      {label && !wrapperItem && (
        <label htmlFor={id} className={classes.label}>
          {label}
        </label>
      )}
      <AntDSelect
        id={id}
        onChange={setValue}
        onBlur={handleBlur}
        value={field.value}
        options={mappedOptions}
        style={styles}
        popupMatchSelectWidth={dropdownWidth}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      />
    </div>
  )

  return wrapperItem
    ? <Item
        label={label}
        labelCol={labelCol}
        wrapperCol={wrapperCol}
        className={containerClassName}
        helperText={helperText}
        validateStatus={hasError ? 'error' : ''}
        help={hasError && error}
        style={{ flex: fullWidth ? 1 : 'initial' }}
        layout={layout}
      >{sel()}</Item>
    : sel()
}

Select.propTypes = {
  containerClassName: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  labelPropName: PropTypes.string,
  helperText: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  valuePropName: PropTypes.string,
  fullWidth: PropTypes.bool,
  labelCol: PropTypes.shape({}),
  labelAlign: PropTypes.string,
  wrapperItem: PropTypes.bool,
  wrapperCol: PropTypes.shape({}),
  wrapperClassName: PropTypes.string,
  layout: PropTypes.oneOf(['horizontal', 'vertical', 'inline']),
  dropdownStyle: PropTypes.shape({}),
  dropdownWidth: PropTypes.number,
}

export { Option, OptGroup }
